<div class="modal-header">
  <!-- `modal-title`: the dialog's name (appService.showModal sets ariaLabelledBy to it). -->
  <fui-heading id="modal-title" [text]="allCommunities ? 'Settings for all communities' : 'Community settings' + (communityTitle ? ' · ' + communityTitle : '')" [level]="5"></fui-heading>
  <button ngbAutofocus type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <fa-icon icon="times" aria-hidden="true"></fa-icon>
  </button>
</div>

<div class="modal-body">
  @if (allCommunities) {
    <p class="text-muted t-13 m-b-20">These apply to every community you're in, except any you've customized.</p>
  } @else {
    <!-- Whether this community follows the member's defaults or has its own settings. -->
    <div class="community-settings__scope m-b-20">
      @if (customized) {
        <span class="t-13">Customized for this community.</span>
        <button type="button" class="btn btn-transparent small-btn" (click)="useDefaults()" [disabled]="resetting || saving">
          @if (resetting) {
            <fa-icon icon="spinner" animation="spin" aria-hidden="true"></fa-icon>
          }
          Use my settings for all communities
        </button>
      } @else {
        <span class="t-13 text-muted">Using your settings for all communities. Changes here apply to this community only.</span>
      }
      <button type="button" class="btn btn-transparent small-btn" (click)="editAllCommunities()" [disabled]="resetting || saving">Edit settings for all communities</button>
    </div>
  }

  <!-- Signature (DENG-847) — one for all communities, overridable per community -->
  <div class="m-b-30">
    <fui-heading id="community-signature-heading" text="Signature" [level]="6"></fui-heading>
    <p class="text-muted t-13 m-b-10" id="community-signature-help">Added to the end of your posts and replies. You can leave it off any one before sending.</p>
    <textarea
      class="form-control community-settings__signature-input"
      id="community-signature"
      rows="2"
      [(ngModel)]="signature"
      [maxlength]="signatureMaxLength"
      placeholder="e.g. — Max, Telecaster enthusiast"
      aria-labelledby="community-signature-heading"
      aria-describedby="community-signature-help community-signature-count"
    ></textarea>
    <div class="d-flex justify-content-between align-items-center">
      <!-- In all-communities mode the signature IS the all-communities one; no switch. -->
      @if (allCommunities) {
        <span class="text-muted t-12">Used in every community where you haven't set a different one.</span>
      } @else {
        <myp-switch
          [active]="signatureForAllCommunities"
          label="Use in all my communities"
          [labelToRight]="true"
          [small]="true"
          (toggle)="signatureForAllCommunities = !signatureForAllCommunities"
        ></myp-switch>
      }
      <span class="text-muted t-12" id="community-signature-count">{{ signature.length }}/{{ signatureMaxLength }}</span>
    </div>
    @if (!allCommunities) {
      <p class="text-muted t-12 m-t-5 mb-0">
        @if (signatureForAllCommunities) {
          Used in every community where you haven't set a different one.
        } @else {
          Used in this community only. Leave it blank to use your all-communities signature here.
        }
      </p>
    }
  </div>

  <!-- Feed ordering — which thread the feed puts on top -->
  <div class="m-b-30" role="group" aria-labelledby="community-settings-feed-order">
    <fui-heading id="community-settings-feed-order" text="Feed order" [level]="6"></fui-heading>
    <p class="text-muted t-13 m-b-10">Sort threads by their newest reply, so an active conversation rises back to the top. Turn this off to order by when each thread was posted.</p>
    <div class="d-block">
      <myp-switch [active]="sortByRecentActivity" label="Sort by newest replies" [labelToRight]="true" (toggle)="sortByRecentActivity = !sortByRecentActivity"></myp-switch>
    </div>
  </div>

  <!-- Where the hub opens — every community at once, so all-communities only; hub viewers only -->
  @if (allCommunities && showStartView) {
    <div class="m-b-30" role="group" aria-labelledby="community-settings-default-feed">
      <fui-heading id="community-settings-default-feed" text="Default feed" [level]="6"></fui-heading>
      <p class="text-muted t-13 m-b-10">
        Open Communities on posts from your favorite channels. Turn this off to open on every post from the communities you've joined. You can switch between them any time.
      </p>
      <div class="d-block">
        <myp-switch [active]="startOnFavorites" label="Start on my favorite channels" [labelToRight]="true" (toggle)="startOnFavorites = !startOnFavorites"></myp-switch>
      </div>
    </div>
  }

  <!-- Channel prefs — which channels the notification can arrive on -->
  <div class="m-b-30" role="group" aria-labelledby="community-settings-deliver">
    <fui-heading id="community-settings-deliver" text="Deliver notifications to" [level]="6"></fui-heading>
    <div class="d-block m-t-10">
      <myp-switch [active]="channelPrefs.email" label="Email" [labelToRight]="true" (toggle)="channelPrefs.email = !channelPrefs.email" extraClasses="m-b-10"></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch [active]="channelPrefs.mobile" label="Push notifications (mobile app)" [labelToRight]="true" (toggle)="channelPrefs.mobile = !channelPrefs.mobile" extraClasses="m-b-10"></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch
        [active]="channelPrefs.desktop"
        label="Notification center + desktop apps"
        [labelToRight]="true"
        (toggle)="channelPrefs.desktop = !channelPrefs.desktop"
        extraClasses="m-b-10"
      ></myp-switch>
    </div>
  </div>

  <!-- Topic prefs — which events even generate a notification -->
  <div class="m-b-20" role="group" aria-labelledby="community-settings-notify">
    <fui-heading id="community-settings-notify" text="Notify me about" [level]="6"></fui-heading>
    <div class="d-block m-t-10">
      <myp-switch
        [active]="topicPrefs.new_posts"
        [label]="allCommunities ? 'New posts in communities I\'ve joined' : 'New posts in this community'"
        [labelToRight]="true"
        (toggle)="topicPrefs.new_posts = !topicPrefs.new_posts"
        extraClasses="m-b-10"
      ></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch [active]="topicPrefs.replies_to_me" label="Replies to me" [labelToRight]="true" (toggle)="topicPrefs.replies_to_me = !topicPrefs.replies_to_me" extraClasses="m-b-10"></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch
        [active]="topicPrefs.replies_in_my_threads"
        label="Replies in threads I started"
        [labelToRight]="true"
        (toggle)="topicPrefs.replies_in_my_threads = !topicPrefs.replies_in_my_threads"
        extraClasses="m-b-10"
      ></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch [active]="topicPrefs.mentions" label="Mentions" [labelToRight]="true" (toggle)="topicPrefs.mentions = !topicPrefs.mentions" extraClasses="m-b-10"></myp-switch>
    </div>
    <div class="d-block">
      <myp-switch
        [active]="topicPrefs.reactions_on_my_posts"
        label="When someone reacts to my post"
        [labelToRight]="true"
        (toggle)="topicPrefs.reactions_on_my_posts = !topicPrefs.reactions_on_my_posts"
        extraClasses="m-b-10"
      ></myp-switch>
    </div>
  </div>
</div>

<div class="modal-footer">
  <!--
    Leave lives on the left, separated from the primary Save action on the right. Only shown
    when the user is currently a member — hidden for the "manage my prefs even though I have
    not joined" case (e.g. legacy FSP users). Uses the same outlined "danger" affordance as
    other destructive-lite actions in the app so it doesn't compete with the Save CTA.

    The member-facing word is Join/Joined/Leave; the code still says follow throughout, because
    `followingCommunities` is the Firestore field and renaming it is a data migration, not copy.
  -->
  @if (isFollowing) {
    <button type="button" class="btn btn-outline-danger m-r-auto" (click)="unfollow()" [disabled]="saving || unfollowing">
      @if (unfollowing) {
        <fa-icon icon="spinner" animation="spin" aria-hidden="true"></fa-icon>
        Leaving…
      } @else {
        Leave community
      }
    </button>
  }
  <button type="button" class="btn btn-blank" (click)="activeModal.dismiss()" [disabled]="saving || unfollowing">Cancel</button>
  <button type="button" class="btn btn-secondary" (click)="save()" [disabled]="saving || unfollowing">
    @if (saving) {
      <fa-icon icon="spinner" animation="spin" aria-hidden="true"></fa-icon>
      Saving…
    } @else {
      <fa-icon icon="check" aria-hidden="true"></fa-icon>
      Save
    }
  </button>
</div>
